/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 20/11/2025 - 08:06:12
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 20/11/2025
    * - Author          : USER
    * - Modification    : 
**/
/**
 * @description: Enhanced User Dashboard with Programs and Live Sessions - FIXED VERSION
 * @author: USER  
 * @created: 06/09/2025
 * @version: 4.1.0 - Fixed null reference errors and program enrollment
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';
import {
  FaCalendarAlt, FaTrophy, FaBell, FaStar, FaCheckCircle,
  FaMapMarkerAlt, FaClock, FaUserFriends, FaMedal, FaFire, FaRegCalendarPlus,
  FaCheck, FaTimes, FaBars, FaSignOutAlt, FaCog, FaHome, FaAward, FaEye,
  FaChevronRight, FaInfoCircle, FaCalendarCheck, FaSpinner, FaUsers,
  FaCalendarPlus, FaCrown, FaExclamationTriangle, FaSync, FaFilter, FaEdit,
  FaGraduationCap, FaMicrophone, FaSpellCheck, FaBookOpen, FaPalette,
  FaFootballBall, FaVideo, FaPlay, FaUserTie, FaBroadcastTower, FaGamepad,
  FaVolumeMute, FaVolumeUp, FaExpand, FaCompress, FaHeart, FaShare
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import api, { eventsAPI, usersAPI, notificationsAPI, liveAPI, programsAPI } from '../services/api';
import { isEventPast } from '../utils/eventDate';
import LiveLeaderboard from '../components/live/LiveLeaderboard';
import UserResults from '../components/live/UserResults';
import LiveSessionPlayer from '../components/live/LiveSessionPlayer';

// HELPER FUNCTIONS - MOVED OUTSIDE COMPONENT
const filterNotificationsForUser = (allNotifications, currentUser, userLoginDate) => {
  if (!currentUser || !allNotifications?.length) {
    return [];
  }

  console.log(`Filtering ${allNotifications.length} notifications for user ${currentUser.email}`);
  console.log('User login date:', userLoginDate);

  return allNotifications.filter(notification => {
    const notificationDate = new Date(notification.createdAt);
    if (notificationDate <= userLoginDate) {
      console.log(`Filtering out old notification: ${notification.title} (${notificationDate} <= ${userLoginDate})`);
      return false;
    }

    const targetUsers = notification.targetUsers?.toLowerCase() || 'all';
    
    let shouldShow = false;
    switch (targetUsers) {
      case 'all':
        shouldShow = true;
        break;
      case 'pending':
        shouldShow = currentUser.status === 'pending';
        break;
      case 'active':
        shouldShow = currentUser.status === 'active';
        break;
      case 'schools':
        shouldShow = currentUser.registrationType === 'school';
        break;
      case 'teams':
        shouldShow = currentUser.registrationType === 'team';
        break;
      case 'individuals':
        shouldShow = currentUser.registrationType === 'individual';
        break;
      case 'specific':
        shouldShow = notification.targetUserIds?.includes(currentUser._id);
        break;
      default:
        if (notification.targetUserIds?.includes(currentUser._id)) {
          shouldShow = true;
        }
    }
    
    return shouldShow;
  });
};

const findUserWinsStrict = (allWinners, currentUser) => {
  if (!currentUser || !allWinners?.length) {
    console.log('No user or winners data available');
    return [];
  }

  console.log(`Checking ${allWinners.length} winners for user ${currentUser.email}`);

  const userWins = allWinners.filter(winner => {
    if (!winner) return false;
    
    // Strategy 1: EXACT User ID match (most reliable)
    if (winner.userId === currentUser._id) {
      console.log(`✓ Winner found by User ID match: ${winner.winnerName}`);
      return true;
    }
    
    // Strategy 2: EXACT Email match (very reliable)
    if (winner.email && 
        winner.email.toLowerCase().trim() === currentUser.email?.toLowerCase().trim()) {
      console.log(`✓ Winner found by email match: ${winner.winnerName}`);
      return true;
    }
    
    // Strategy 3: EXACT name matching ONLY (no partial matching)
    const winnerName = (winner.winnerName || '').toLowerCase().trim();
    const userNames = [
      currentUser.name,
      currentUser.teamName, 
      currentUser.schoolName
    ].filter(Boolean).map(name => name.toLowerCase().trim());
    
    // ONLY exact matches - no fuzzy matching to prevent false positives
    for (const userName of userNames) {
      if (userName.length > 2 && winnerName === userName) {
        console.log(`✓ Winner found by EXACT name match: "${winnerName}" === "${userName}"`);
        return true;
      }
    }
    
    return false;
  });
  
  console.log(`✓ Found ${userWins.length} confirmed wins for user ${currentUser.name || currentUser.email}`);
  return userWins;
};

const Dashboard = () => {
  const { user: clerkUser, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [liveSessions, setLiveSessions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);
  
  // Data states
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});
  const [winners, setWinners] = useState([]);
  
  // UI states
  const [showNotifications, setShowNotifications] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedLiveSession, setSelectedLiveSession] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');
  const [myEventFilter, setMyEventFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  
  // Live session states
  const [likes, setLikes] = useState(0);

  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    grade: ''
  });

  // Icon mapping for programs
  const iconMapping = {
    'FaCrown': FaCrown,
    'FaMicrophone': FaMicrophone,
    'FaUsers': FaUsers,
    'FaSpellCheck': FaSpellCheck,
    'FaBookOpen': FaBookOpen,
    'FaPalette': FaPalette,
    'FaFootballBall': FaFootballBall,
    'FaGraduationCap': FaGraduationCap
  };

  // Get program icon
  const getProgramIcon = (iconName) => {
    return iconMapping[iconName] || FaGraduationCap;
  };

  // Color schemes for programs
  const getColorClasses = (category) => {
    const schemes = {
      core: 'from-green-500 to-emerald-600',
      academic: 'from-blue-500 to-blue-600',
      cultural: 'from-yellow-500 to-yellow-600',
      creative: 'from-pink-500 to-pink-600',
      premium: 'from-red-500 to-red-600',
      sports: 'from-orange-500 to-orange-600'
    };
    return schemes[category] || 'from-gray-500 to-gray-600';
  };
  
  // Utility function to safely format dates
  const formatDate = (dateInput) => {
    if (!dateInput) return 'Date not set';
    
    try {
      let date;
      
      // Handle different date formats
      if (typeof dateInput === 'string') {
        // Try parsing ISO string first
        date = new Date(dateInput);
        
        // If invalid, try parsing as timestamp
        if (isNaN(date.getTime())) {
          const timestamp = parseInt(dateInput);
          if (!isNaN(timestamp)) {
            date = new Date(timestamp);
          }
        }
      } else if (typeof dateInput === 'number') {
        // Handle timestamp
        date = new Date(dateInput);
      } else if (dateInput instanceof Date) {
        date = dateInput;
      } else {
        return 'Invalid date format';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date error';
    }
  };

  // Utility function for relative time
  const formatRelativeDate = (dateInput) => {
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'Unknown date';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      return 'Unknown date';
    }
  };

  // Use shared isEventPast util (imported at top)

  // FIXED: Safe function to check if user is registered for an event
  const isRegisteredForEvent = (event) => {
    if (!event || !event._id || !user || !user._id) return false;
    
    // Check in myEvents array
    const isInMyEvents = myEvents.some(myEvent => 
      myEvent && myEvent._id && myEvent._id === event._id
    );
    
    // Check in event participants
    const isInParticipants = event.participants && Array.isArray(event.participants) && 
      event.participants.some(participant => 
        participant && 
        (participant.userId === user._id || 
         participant.userEmail === user.email)
      );
    
    return isInMyEvents || isInParticipants;
  };

  // FIXED: Better enrollment checking with multiple strategies
  const isEnrolledInProgram = (program) => {
    if (!program || !program._id || !user || !user._id) {
      return false;
    }
    
    // Strategy 1: Check in enrolledPrograms state (frontend state)
    const isInEnrolledPrograms = enrolledPrograms.some(enrolledProgram => {
      if (!enrolledProgram) return false;
      return enrolledProgram._id === program._id || enrolledProgram.programId === program._id;
    });
    
    // Strategy 2: Check in program's enrolledUsers array (from server data)
    const isInProgramUsers = program.enrolledUsers && Array.isArray(program.enrolledUsers) && 
      program.enrolledUsers.some(enrolledUser => {
        if (!enrolledUser) return false;
        
        const userIdMatch = enrolledUser.userId === user._id;
        const emailMatch = enrolledUser.userEmail === user.email;
        const activeStatus = enrolledUser.status === 'enrolled' || enrolledUser.status === 'waitlist';
        
        return (userIdMatch || emailMatch) && activeStatus;
      });
    
    const result = isInEnrolledPrograms || isInProgramUsers;
    
    if (result) {
    }
    
    return result;
  };

  // FIXED: Program Modal enrollment status display with comprehensive status checking
  const getProgramEnrollmentStatus = (program) => {
    if (!program) return null;
    
    const isEnrolled = isEnrolledInProgram(program);
    
    if (isEnrolled) {
      // Check if user is on waitlist
      const userEnrollment = program.enrolledUsers?.find(eu => 
        (eu.userId === user._id || eu.userEmail === user.email) && 
        (eu.status === 'enrolled' || eu.status === 'waitlist')
      );
      
      if (userEnrollment?.status === 'waitlist') {
        return {
          enrolled: true,
          status: 'waitlist',
          message: 'On Waitlist',
          className: 'bg-yellow-100 text-yellow-800'
        };
      }
      
      return {
        enrolled: true,
        status: 'enrolled',
        message: 'Enrolled',
        className: 'bg-green-100 text-green-800'
      };
    }
    
    // Check capacity and enrollment status
    if (program.enrollmentStatus === 'closed') {
      return {
        enrolled: false,
        status: 'closed',
        message: 'Enrollment Closed',
        className: 'bg-red-100 text-red-800'
      };
    }
    
    if ((program.currentEnrollment || 0) >= (program.capacity || 0)) {
      if (program.enrollmentStatus === 'waitlist') {
        return {
          enrolled: false,
          status: 'waitlist_available',
          message: 'Join Waitlist',
          className: 'bg-yellow-100 text-yellow-800'
        };
      } else {
        return {
          enrolled: false,
          status: 'full',
          message: 'Program Full',
          className: 'bg-red-100 text-red-800'
        };
      }
    }
    
    return {
      enrolled: false,
      status: 'available',
      message: 'Available',
      className: 'bg-blue-100 text-blue-800'
    };
  };


  // FIXED: fetchUserData function - removed dependency on user to prevent loops
  const fetchUserData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setRefreshing(true);

    try {
      // Get user's last login date (or account creation date) - safely
      const userLoginDate = user ? new Date(user.lastLogin || user.createdAt || Date.now()) : new Date();

      // Fetch all data in parallel with individual error handling
      const fetchPromises = [
        eventsAPI.getAll().catch(err => ({ success: false, events: [], error: err.message })),
        eventsAPI.getUserEvents().catch(err => ({ success: false, events: [], error: err.message })),
        notificationsAPI.getAll().catch(err => ({ success: false, notifications: [], error: err.message })),
        usersAPI.getStats().catch(err => ({ success: false, stats: {}, error: err.message })),
        usersAPI.getWinners().catch(err => ({ success: false, winners: [], error: err.message })),
        programsAPI.getAll().catch(err => ({ success: false, programs: [], error: err.message })),
        programsAPI.getUserPrograms().catch(err => ({ success: false, programs: [], error: err.message }))
      ];

      const [
        eventsData,
        myEventsData,
        notificationsData,
        statsData,
        winnersData,
        programsData,
        userProgramsData
      ] = await Promise.all(fetchPromises);

      // Process events data
      const processedEvents = (eventsData.events || []).filter(event => event && event._id);

      // Process user events data
      const processedMyEvents = (myEventsData.events || []).filter(event => event && event._id);

      // STRICT: Filter notifications by date and target (only if user exists)
      const allNotifications = (notificationsData.notifications || []).filter(notif => notif && notif._id);
      const filteredNotifications = user ? filterNotificationsForUser(allNotifications, user, userLoginDate) : [];

      // STRICT: Process winners with exact matching only (only if user exists)
      const allWinners = (winnersData.winners || []).filter(winner => winner && winner._id);
      const userWins = user ? findUserWinsStrict(allWinners, user) : [];

      // Process programs data
      const processedPrograms = (programsData.programs || []).filter(program => program && program._id);
      const processedUserPrograms = (userProgramsData.programs || []).filter(program => program && program._id);

      // Fetch live sessions separately
      try {
        const liveSessionsData = await liveAPI.getLiveSessions();
        const validLiveSessions = (liveSessionsData.sessions || []).filter(session => session && session._id);
        setLiveSessions(validLiveSessions);
      } catch (error) {
        setLiveSessions([]);
      }

      // Update state with FILTERED data
      setEvents(processedEvents);
      setMyEvents(processedMyEvents);
      setNotifications(filteredNotifications); // ONLY relevant notifications
      setWinners(userWins); // ONLY user's actual wins
      setPrograms(processedPrograms);
      setEnrolledPrograms(processedUserPrograms);

      // Calculate stats with user wins only (only if user exists)
      const recentEvents = processedMyEvents.filter(e =>
        e.createdAt && new Date(e.createdAt) > new Date(Date.now() - 30*24*60*60*1000)
      );

      const calculatedStats = {
        registeredEvents: processedMyEvents.length,
        wins: userWins.length, // Only actual user wins
        totalPoints: userWins.length * 10,
        monthlyPoints: userWins.filter(win =>
          win.createdAt && new Date(win.createdAt) > new Date(Date.now() - 30*24*60*60*1000)
        ).length * 10,
        rank: userWins.length >= 5 ? 'Champion' :
              userWins.length >= 3 ? 'Expert' :
              userWins.length >= 1 ? 'Winner' : 'Participant',
        rating: Math.min(4.0 + (userWins.length * 0.2), 5.0),
        recentEventsCount: recentEvents.length,
        userWins: userWins,
        lastActivity: processedMyEvents.length > 0 ?
          Math.max(...processedMyEvents.map(e => new Date(e.createdAt || 0).getTime())) : null,
        completedEvents: processedMyEvents.filter(e =>
          e.date && new Date(e.date) <= new Date()
        ).length,
        upcomingEvents: processedMyEvents.filter(e =>
          e.date && new Date(e.date) > new Date()
        ).length,
        enrolledPrograms: processedUserPrograms.length
      };

      setStats(calculatedStats);

      // Show success message ONLY for NEW wins since last login (only if user exists)
      if (user && userWins.length > 0) {
        const newWins = userWins.filter(win =>
          new Date(win.createdAt || win.updatedAt) > userLoginDate
        );

        if (newWins.length > 0) {
          const sessionKey = `new_wins_notified_${user._id}_${userLoginDate.getTime()}`;
          if (!sessionStorage.getItem(sessionKey)) {
            toast.success(`Congratulations! You have ${newWins.length} new win${newWins.length > 1 ? 's' : ''}!`);
            sessionStorage.setItem(sessionKey, 'true');
          }
        }
      }

    } catch (error) {
      console.error('Dashboard: Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data. Please refresh the page.');

      // Set fallback data to prevent crashes
      setEvents([]);
      setMyEvents([]);
      setNotifications([]);
      setWinners([]);
      setPrograms([]);
      setEnrolledPrograms([]);
      setStats({
        registeredEvents: 0,
        wins: 0,
        totalPoints: 0,
        monthlyPoints: 0,
        rank: 'Participant',
        rating: 4.0,
        userWins: [],
        enrolledPrograms: 0
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // Removed user dependency to prevent loops


  // FIXED: Fetch user profile from backend - prevent loops
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isLoaded || !clerkUser) return;

      try {
        const token = await getToken();
        const response = await api.get('/auth/profile', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response?.user) {
          setUser(response.user);
        }
      } catch (error) {
        console.error('Dashboard: Failed to fetch user profile:', error);
      }
    };

    fetchUserProfile();
  }, [isLoaded, clerkUser?.id, getToken]); // Only depend on clerkUser.id to prevent loops

  // FIXED: Fetch notifications only - prevent loops
  const fetchNotifications = useCallback(async () => {
    if (!user) return; // Don't fetch if no user

    try {
      const data = await notificationsAPI.getAll();
      const validNotifications = (data.notifications || []).filter(notif => notif && notif._id);

      // Only update if user exists and we have notifications
      if (user) {
        const userLoginDate = new Date(user.lastLogin || user.createdAt || Date.now());
        const filteredNotifications = filterNotificationsForUser(validNotifications, user, userLoginDate);
        setNotifications(filteredNotifications);
      }
    } catch (error) {
      console.error('Dashboard: Failed to fetch notifications:', error);
    }
  }, [user]); // Depend on user but safely

  // FIXED: Initial data load - prevent loops with proper dependencies
  useEffect(() => {
    if (user && user._id) {
      fetchUserData();

      // Initialize profile data
      setProfileData({
        name: user.name || user.teamName || user.schoolName || '',
        email: user.email || '',
        phone: user.phone || '',
        school: user.schoolName || '',
        grade: user.grade || ''
      });

      // Set up intervals - only once per user
      const liveSessionsInterval = setInterval(async () => {
        try {
          const data = await liveAPI.getLiveSessions();
          const validSessions = (data.sessions || []).filter(session => session && session._id);
          setLiveSessions(validSessions);
        } catch (error) {
          console.error('Dashboard: Failed to fetch live sessions:', error);
        }
      }, 10000);

      const notificationsInterval = setInterval(fetchNotifications, 30000);

      // Cleanup intervals
      return () => {
        clearInterval(liveSessionsInterval);
        clearInterval(notificationsInterval);
      };
    }
  }, [user?._id]); // Only depend on user._id to prevent loops

  // Handle manual refresh
  const handleRefresh = () => {
    fetchUserData(false);
  };

  // FIXED: Handle event registration with better error handling
  const handleEventRegistration = async (eventId) => {
    if (!eventId) {
      toast.error('Invalid event ID');
      return;
    }

    if (user?.status !== 'active') {
      toast.error('Please complete your registration and payment to register for events');
      return;
    }

    const event = events.find(e => e && e._id === eventId);
    if (!event) {
      toast.error('Event not found');
      return;
    }

    // Prevent registration for past events
    if (isEventPast(event)) {
      toast.info('This event has already ended and cannot be registered for.');
      return;
    }

    if (isRegisteredForEvent(event)) {
      toast.info('You are already registered for this event');
      return;
    }

    try {
      setLoading(true);
      const data = await eventsAPI.register(eventId);
      
      if (data.success) {
        // Update myEvents state
        setMyEvents(prev => [...prev, event]);
        
        // Update events state to show registration status
        setEvents(prev => prev.map(e => 
          e._id === eventId 
            ? { 
                ...e, 
                participants: [...(e.participants || []), { 
                  userId: user._id,
                  userEmail: user.email,
                  registeredAt: new Date().toISOString()
                }],
                isRegistered: true
              }
            : e
        ));
        
        // Update stats to reflect new registration
        setStats(prev => ({
          ...prev,
          registeredEvents: (prev.registeredEvents || 0) + 1,
          recentEventsCount: (prev.recentEventsCount || 0) + 1
        }));
        
        toast.success('Successfully registered for event!');
        setShowEventModal(false);
        
        // Refresh data to ensure consistency
        setTimeout(() => {
          fetchUserData(false);
        }, 1000);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to register for event');
    } finally {
      setLoading(false);
    }
  };

  // IMPROVED: Handle program enrollment with better state management and real-time updates
  const handleProgramEnrollment = async (programId) => {
    if (!programId) {
      toast.error('Invalid program ID');
      return;
    }

    if (!user) {
      toast.error('Please login to enroll in programs');
      return;
    }

    const program = programs.find(p => p && p._id === programId);
    if (!program) {
      toast.error('Program not found');
      return;
    }

    if (isEnrolledInProgram(program)) {
      toast.info('You are already enrolled in this program');
      return;
    }

    try {
      setLoading(true);
      
      const response = await programsAPI.enroll(programId);
      
      if (response.success) {
        // CRITICAL FIX: Use the updated program data from the server response
        const updatedProgram = response.program || program;
        
        // IMMEDIATE STATE UPDATE: Add to enrolledPrograms state right away
        setEnrolledPrograms(prev => {
          // Avoid duplicates
          const isAlreadyInState = prev.some(p => p._id === programId);
          if (isAlreadyInState) {
            return prev.map(p => p._id === programId ? updatedProgram : p);
          }
          
          return [...prev, updatedProgram];
        });
        
        // Update programs state with the server data to reflect enrollment count changes
        setPrograms(prev => prev.map(p => 
          p._id === programId ? updatedProgram : p
        ));
        
        // Update stats to reflect new enrollment
        setStats(prev => ({
          ...prev,
          enrolledPrograms: (prev.enrolledPrograms || 0) + 1
        }));
        
        toast.success('Successfully enrolled in program! We will contact you with more details.');
        setShowProgramModal(false);
        
        // BACKGROUND REFRESH: Refresh data in the background to ensure consistency
        setTimeout(() => {
          fetchUserData(false);
        }, 1000);
        
      } else {
        throw new Error(response.message || 'Enrollment failed');
      }
    } catch (error) {
      
      // Handle specific error cases
      if (error.message.includes('already enrolled')) {
        toast.info('You are already enrolled in this program');
        // Refresh data to sync state
        fetchUserData(false);
      } else if (error.message.includes('full capacity')) {
        toast.error('Sorry, this program is currently full');
      } else {
        toast.error(error.message || 'Failed to enroll in program');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle event unregistration
  const handleEventUnregister = async (eventId) => {
    if (!eventId) {
      toast.error('Invalid event ID');
      return;
    }

    if (!user) {
      toast.error('Please login to unregister from events');
      return;
    }

    const event = myEvents.find(e => e && e._id === eventId);
    if (!event) {
      toast.error('Event not found in your registrations');
      return;
    }

    try {
      setLoading(true);
      const response = await eventsAPI.unregister(eventId);

      if (response.success) {
        // Remove from myEvents state
        setMyEvents(prev => prev.filter(e => e._id !== eventId));

        // Update events state to remove registration status
        setEvents(prev => prev.map(e =>
          e._id === eventId
            ? {
                ...e,
                participants: (e.participants || []).filter(p => p.userId !== user._id),
                isRegistered: false
              }
            : e
        ));

        // Update stats
        setStats(prev => ({
          ...prev,
          registeredEvents: Math.max(0, (prev.registeredEvents || 0) - 1)
        }));

        toast.success('Successfully unregistered from event');
        setShowEventModal(false);

        // Refresh data
        setTimeout(() => {
          fetchUserData(false);
        }, 1000);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to unregister from event');
    } finally {
      setLoading(false);
    }
  };

  // Handle program unenrollment
  const handleProgramUnenroll = async (programId) => {
    if (!programId) {
      toast.error('Invalid program ID');
      return;
    }

    if (!user) {
      toast.error('Please login to unenroll from programs');
      return;
    }

    const program = enrolledPrograms.find(p => p && p._id === programId);
    if (!program) {
      toast.error('Program not found in your enrollments');
      return;
    }

    try {
      setLoading(true);

      const response = await programsAPI.unenroll(programId);

      if (response.success) {
        // Remove from enrolledPrograms state
        setEnrolledPrograms(prev => prev.filter(p => p._id !== programId));

        // Update programs state
        setPrograms(prev => prev.map(p =>
          p._id === programId
            ? {
                ...p,
                enrolledUsers: (p.enrolledUsers || []).filter(e => e.userId?._id !== user._id),
                currentEnrollment: Math.max(0, (p.currentEnrollment || 0) - 1)
              }
            : p
        ));

        // Update stats
        setStats(prev => ({
          ...prev,
          enrolledPrograms: Math.max(0, (prev.enrolledPrograms || 0) - 1)
        }));

        toast.success('Successfully unenrolled from program');
        setShowProgramModal(false);

        // Refresh data
        setTimeout(() => {
          fetchUserData(false);
        }, 1000);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to unenroll from program');
    } finally {
      setLoading(false);
    }
  };

  // Handle joining live session
  const handleJoinLiveSession = (session) => {
    setSelectedLiveSession(session);
    setLikes(session.likes || Math.floor(Math.random() * 100));
    setShowLiveModal(true);
  };

  // Handle live session actions
  const handleLike = () => {
    toast.success('Liked the session!');
  };

  const handleShare = () => {
    toast.success('Session link shared!');
  };


  // Mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev => prev.map(notif => 
        notif._id === notificationId ? { ...notif, read: true } : notif
      ));
    } catch (error) {
    }
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const updatedData = await usersAPI.updateProfile(profileData);

      if (updatedData.success) {
        toast.success('Profile updated successfully!');
        setEditingProfile(false);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      await signOut();
      toast.success('Logged out successfully!');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  // FIXED: Update the getUserStats function to handle programs properly
  const getUserStats = () => {
    const userWins = stats.userWins || [];
    const recentEvents = myEvents.filter(e => 
      e.createdAt && new Date(e.createdAt) > new Date(Date.now() - 30*24*60*60*1000)
    );

    return [
      { 
        icon: FaCalendarAlt, 
        label: 'Events Registered',
        value: myEvents.length,
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-500/10',
        change: recentEvents.length > 0 ? `+${recentEvents.length} this month` : 'No recent events'
      },
      { 
        icon: FaTrophy, 
        label: 'Competitions Won', 
        value: userWins.length,
        color: 'from-yellow-500 to-orange-500',
        bgColor: 'bg-yellow-500/10',
        change: userWins.length > 0 ? 'Congratulations!' : 'Keep participating!'
      },
      { 
        icon: FaGraduationCap, 
        label: 'Programs Enrolled',
        value: enrolledPrograms?.length || 0,
        color: 'from-green-500 to-emerald-600',
        bgColor: 'bg-green-500/10',
        change: `${programs.filter(p => p && !p.isArchived && p.isActive).length} available`
      },
      { 
        icon: FaVideo, 
        label: 'Live Sessions', 
        value: liveSessions.filter(s => s && s.isLive).length,
        color: 'from-red-500 to-red-600',
        bgColor: 'bg-red-500/10',
        change: liveSessions.filter(s => s && s.isLive).length > 0 ? 'Live now!' : 'None active'
      }
    ];
  };

  const dashboardStats = getUserStats();

  const sidebarItems = [
    { id: 'dashboard', icon: FaHome, label: 'Dashboard' },
    { id: 'events', icon: FaCalendarAlt, label: 'Events' },
    { id: 'my-events', icon: FaCalendarCheck, label: 'My Events' },
    { id: 'programs', icon: FaGraduationCap, label: 'Programs' },
    //{ id: 'live-sessions', icon: FaVideo, label: 'Live Sessions' },
    { id: 'notifications', icon: FaBell, label: 'Notifications' },
    //{ id: 'achievements', icon: FaTrophy, label: 'Achievements' },
    { id: 'profile', icon: FaUsers, label: 'Profile' },
    { id: 'settings', icon: FaCog, label: 'Settings' }
  ];

  // Handle navigation with automatic sidebar close on mobile
  const handleNavigation = (tabId) => {
    setActiveTab(tabId);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Get breadcrumb path
  const getBreadcrumbs = () => {
    const currentItem = sidebarItems.find(item => item.id === activeTab);
    return [
      { label: 'Dashboard', id: 'dashboard' },
      ...(activeTab !== 'dashboard' ? [{ label: currentItem?.label || 'Unknown', id: activeTab }] : [])
    ];
  };

  const getStatusMessage = () => {
    switch (user?.status) {
      case 'pending':
        return {
          type: 'warning',
          message: 'Your registration is under review. You can browse events but cannot register until approved.',
          action: 'Contact support for assistance with payment verification.'
        };
      case 'active':
        return {
          type: 'success',
          message: 'Your account is active! You can register for events and access all features.',
          action: null
        };
      case 'inactive':
        return {
          type: 'error',
          message: 'Your account is inactive. Please contact support to reactivate.',
          action: 'Contact support to resolve this issue.'
        };
      default:
        return {
          type: 'info',
          message: 'Welcome to ACD! Complete your profile to get started.',
          action: 'Update your profile information.'
        };
    }
  };

  const statusInfo = getStatusMessage();

  // FIXED: Filter events with proper null checks
  const filteredEvents = events.filter(event => {
    if (!event || !event._id) return false;
    
    if (eventFilter === 'all') return true;
    if (eventFilter === 'featured') return event.featured;
    if (eventFilter === 'upcoming') return event.date && new Date(event.date) > new Date();
    if (eventFilter === 'available') {
      return !isRegisteredForEvent(event);
    }
    if (eventFilter === 'registered') {
      return isRegisteredForEvent(event);
    }
    return event.type === eventFilter;
  });

  // FIXED: Filter my events with proper null checks
  const filteredMyEvents = myEvents.filter(event => {
    if (!event || !event._id) return false;
    
    if (myEventFilter === 'all') return true;
    if (myEventFilter === 'upcoming') return event.date && new Date(event.date) > new Date();
    if (myEventFilter === 'completed') return event.date && new Date(event.date) <= new Date();
    return event.type === myEventFilter;
  });

  // FIXED: Filter programs with better enrollment checking and null safety
  const filteredPrograms = programs.filter(program => {
    if (!program || !program._id || !program.isActive || program.isArchived) return false;
    
    if (programFilter === 'all') return true;
    if (programFilter === 'featured') return program.isFeatured;
    if (programFilter === 'free') return !program.price || program.price.amount === 0;
    if (programFilter === 'premium') return program.price && program.price.amount > 0;
    if (programFilter === 'enrolled') {
      return isEnrolledInProgram(program);
    }
    if (programFilter === 'available') {
      return !isEnrolledInProgram(program);
    }
    return program.category === programFilter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const activeLiveSessions = liveSessions.filter(s => s.isLive);

  // Don't render if still loading or no user
  if (!isLoaded || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-8 h-8 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex pt-20">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - FIXED VERSION */}
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: sidebarOpen ? 0 : -250 }}
        className={`${
          sidebarOpen ? 'w-64' : 'w-16'
        } bg-white border-r border-gray-200 shadow-lg transition-all duration-300 flex flex-col fixed h-full z-30 lg:relative lg:z-auto`}
        style={{
          // CRITICAL: Account for the fixed header height
          top: '80px', // Header height
          height: 'calc(100vh - 80px)', // Full height minus header
        }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h2 className="text-lg font-bold text-gray-900">ACD Dashboard</h2>
                <p className="text-sm text-gray-600 capitalize">{user?.registrationType || 'Member'}</p>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-2xl hover:bg-gray-100 transition-colors"
            >
              <FaBars className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      
        {/* User Info */}
        {sidebarOpen && (
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold">
                  {(user?.name || user?.teamName || user?.schoolName || 'U')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {user?.name || user?.teamName || user?.schoolName || 'User'}
                </p>
                <p className="text-xs text-gray-600 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}
      
        {/* FIXED: Scrollable Navigation Container */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <nav className="p-4 space-y-2">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const hasNotification = item.id === 'notifications' && unreadCount > 0;
              const hasLive = item.id === 'live-sessions' && activeLiveSessions.length > 0;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-2xl transition-all duration-200 relative ${
                    activeTab === item.id
                      ? 'bg-green-100 text-green-700 border-l-4 border-green-500'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="font-medium truncate">{item.label}</span>}
                  
                  {/* Notification badge */}
                  {hasNotification && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                  
                  {/* Live indicator */}
                  {hasLive && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      
        {/* FIXED: Logout Button - Always Visible at Bottom */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            onClick={logout}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-2xl text-red-600 hover:bg-red-50 transition-all duration-200"
          >
            <FaSignOutAlt className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-2xl hover:bg-gray-100 transition-colors lg:hidden"
              >
                <FaBars className="w-5 h-5 text-gray-600" />
              </button>
              
              {/* Breadcrumb Navigation */}
              <div className="flex items-center space-x-2">
                {getBreadcrumbs().map((crumb, index) => (
                  <div key={crumb.id} className="flex items-center space-x-2">
                    {index > 0 && <FaChevronRight className="w-3 h-3 text-gray-400" />}
                    <button
                      onClick={() => handleNavigation(crumb.id)}
                      className={`text-sm font-medium transition-colors ${
                        index === getBreadcrumbs().length - 1
                          ? 'text-gray-900 cursor-default'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      disabled={index === getBreadcrumbs().length - 1}
                    >
                      {crumb.label}
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Live Session Alert */}
              {activeLiveSessions.length > 0 && activeTab !== 'live-sessions' && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  onClick={() => handleNavigation('live-sessions')}
                  className="flex items-center space-x-2 px-3 py-1 bg-red-500 text-white rounded-full text-xs font-medium animate-pulse hover:bg-red-600 transition-colors"
                >
                  <FaVideo className="w-3 h-3" />
                  <span>{activeLiveSessions.length} Live Now</span>
                </motion.button>
              )}
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 rounded-2xl hover:bg-gray-100 transition-colors"
                title="Refresh data"
              >
                <FaSync className={`w-4 h-4 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-2xl hover:bg-gray-100 transition-colors"
                >
                  <FaBell className="w-5 h-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-2xl shadow-xl z-50"
                    >
                      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllNotificationsAsRead}
                            className="text-xs text-green-600 hover:text-green-700 font-medium"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.slice(0, 5).map((notification) => (
                            <div
                              key={notification._id}
                              className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                                !notification.read ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => markNotificationAsRead(notification._id)}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`w-2 h-2 rounded-full mt-2 ${
                                  notification.type === 'success' ? 'bg-green-400' :
                                  notification.type === 'warning' ? 'bg-yellow-400' :
                                  notification.type === 'error' ? 'bg-red-400' : 'bg-blue-400'
                                }`} />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900 text-sm">{notification.title}</p>
                                  <p className="text-gray-600 text-sm mt-1">{notification.message}</p>
                                  <p className="text-gray-500 text-xs mt-2">
                                    {new Date(notification.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500">
                            <FaBell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p>No notifications yet</p>
                          </div>
                        )}
                      </div>
                      {notifications.length > 5 && (
                        <div className="p-4 border-t border-gray-200">
                          <button
                            onClick={() => {
                              handleNavigation('notifications');
                              setShowNotifications(false);
                            }}
                            className="w-full text-center text-green-600 hover:text-green-700 text-sm font-medium"
                          >
                            View all notifications
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Status Badge */}
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                user?.status === 'active' ? 'bg-green-100 text-green-800' :
                user?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {user?.status || 'Pending'}
              </div>

              {/* User Avatar */}
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {(user?.name || user?.teamName || user?.schoolName || 'U')[0].toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">

          {/* Status Alert */}
          {user?.status !== 'active' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-2xl border mb-6 ${
                statusInfo.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                statusInfo.type === 'error' ? 'bg-red-50 border-red-200' :
                'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex items-start space-x-3">
                <FaInfoCircle className={`w-5 h-5 mt-0.5 ${
                  statusInfo.type === 'warning' ? 'text-yellow-600' :
                  statusInfo.type === 'error' ? 'text-red-600' :
                  'text-blue-600'
                }`} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    statusInfo.type === 'warning' ? 'text-yellow-800' :
                    statusInfo.type === 'error' ? 'text-red-800' :
                    'text-blue-800'
                  }`}>
                    Account Status: {user?.status || 'Pending'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    statusInfo.type === 'warning' ? 'text-yellow-700' :
                    statusInfo.type === 'error' ? 'text-red-700' :
                    'text-blue-700'
                  }`}>
                    {statusInfo.message}
                  </p>
                  {statusInfo.action && (
                    <p className={`text-sm mt-2 font-medium ${
                      statusInfo.type === 'warning' ? 'text-yellow-800' :
                      statusInfo.type === 'error' ? 'text-red-800' :
                      'text-blue-800'
                    }`}>
                      Action needed: {statusInfo.action}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <FaSpinner className="w-8 h-8 text-green-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading your dashboard...</p>
              </div>
            </div>
          )}

          {/* Main Dashboard Content */}
          {!loading && activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Welcome Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      Welcome back, <span className="text-green-600">
                        {user?.name || user?.teamName || user?.schoolName || 'Champion'}
                      </span>!
                    </h1>
                    <p className="text-gray-600">
                      {user?.registrationType === 'school' ? 'School Administrator' :
                       user?.registrationType === 'team' ? 'Team Coach' : 'Debater'} • 
                      Ready to debate and excel!
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <FaStar className="w-5 h-5 text-yellow-500" />
                      <span className="font-semibold text-gray-900">
                        {stats.rating || '4.0'} Rating
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {dashboardStats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-2xl ${stat.bgColor}`}>
                          <Icon className={`w-6 h-6 ${
                            stat.color === 'from-blue-500 to-blue-600' ? 'text-blue-600' :
                            stat.color === 'from-yellow-500 to-orange-500' ? 'text-yellow-600' :
                            stat.color === 'from-green-500 to-emerald-600' ? 'text-green-600' :
                            'text-red-600'
                          }`} />
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-2xl">
                          {stat.change}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {stat.value}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {stat.label}
                      </p>
                    </motion.div>
                  );
                })}
              </div>

              {/* Quick Actions & Recent Activities */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Live Sessions Alert */}
                {activeLiveSessions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="lg:col-span-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-3xl p-6 text-white shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                          <FaVideo className="w-6 h-6 animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">Live Sessions Active!</h3>
                          <p className="text-red-100">
                            {activeLiveSessions.length} live session{activeLiveSessions.length > 1 ? 's' : ''} happening now
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleNavigation('live-sessions')}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 flex items-center space-x-2"
                      >
                        <FaPlay className="w-4 h-4" />
                        <span>Join Now</span>
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Recent Events */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Events</h3>
                    <button
                      onClick={() => handleNavigation('events')}
                      className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center space-x-1"
                    >
                      <span>View all</span>
                      <FaChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {events.slice(0, 3).map((event) => (
                      <div key={event._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-100 rounded-2xl flex items-center justify-center">
                            <FaCalendarAlt className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{event.title}</p>
                            <p className="text-gray-600 text-xs">
                              {new Date(event.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          myEvents.some(me => me._id === event._id) 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {myEvents.some(me => me._id === event._id) ? 'Registered' : 'Available'}
                        </span>
                      </div>
                    ))}
                    {events.length === 0 && (
                      <div className="text-center py-4 text-gray-500">
                        <FaCalendarAlt className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No events available</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Programs Overview */}
                <motion.div
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Programs</h3>
                    <button
                      onClick={() => handleNavigation('programs')}
                      className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center space-x-1"
                    >
                      <span>View all</span>
                      <FaChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {programs.filter(p => p.isFeatured && p.isActive && !p.isArchived).slice(0, 3).map((program) => {
                      const Icon = getProgramIcon(program.icon);
                      return (
                        <div key={program._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-100 rounded-2xl flex items-center justify-center">
                              <Icon className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{program.title}</p>
                              <p className="text-gray-600 text-xs">{program.duration}</p>
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Featured
                          </span>
                        </div>
                      );
                    })}
                    {programs.filter(p => p.isFeatured).length === 0 && (
                      <div className="text-center py-4 text-gray-500">
                        <FaGraduationCap className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No featured programs</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Quick Actions */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => handleNavigation('events')}
                      className="w-full flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-2xl hover:bg-green-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FaCalendarPlus className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-green-700">Browse Events</span>
                      </div>
                      <FaChevronRight className="w-4 h-4 text-green-600" />
                    </button>
                    
                    <button
                      onClick={() => handleNavigation('programs')}
                      className="w-full flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-2xl hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FaGraduationCap className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-blue-700">View Programs</span>
                      </div>
                      <FaChevronRight className="w-4 h-4 text-blue-600" />
                    </button>
                    
                    {activeLiveSessions.length > 0 && (
                      <button
                        onClick={() => handleNavigation('live-sessions')}
                        className="w-full flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-2xl hover:bg-red-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <FaVideo className="w-4 h-4 text-red-600" />
                          <span className="font-medium text-red-700">Join Live Session</span>
                        </div>
                        <FaChevronRight className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleNavigation('achievements')}
                      className="w-full flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-2xl hover:bg-yellow-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FaTrophy className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium text-yellow-700">View Achievements</span>
                      </div>
                      <FaChevronRight className="w-4 h-4 text-yellow-600" />
                    </button>
                  </div>
                </motion.div>
              </div>
            </div>
          )}

          {/* Programs Tab */}
          {!loading && activeTab === 'programs' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Training Programs</h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <FaFilter className="w-4 h-4 text-gray-500" />
                    <select
                      value={programFilter}
                      onChange={(e) => setProgramFilter(e.target.value)}
                      className="border border-gray-300 rounded-2xl px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">All Programs</option>
                      <option value="enrolled">My Enrolled Programs</option>
                      <option value="available">Available to Enroll</option>
                      <option value="featured">Featured</option>
                      <option value="core">Core</option>
                      <option value="academic">Academic</option>
                      <option value="cultural">Cultural</option>
                      <option value="creative">Creative</option>
                      <option value="premium">Premium</option>
                      <option value="free">Free</option>
                    </select>
                  </div>
                  <span className="text-green-600 font-semibold">{filteredPrograms.length} Programs</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredPrograms.map((program) => {
                  const Icon = getProgramIcon(program.icon);
                  return (
                    <motion.div
                      key={program._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-6 rounded-3xl border-2 transition-all duration-300 cursor-pointer hover:shadow-md ${
                        program.isFeatured 
                          ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-50' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedProgram(program);
                        setShowProgramModal(true);
                      }}
                    >
                      {program.isFeatured && (
                        <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                          FEATURED
                        </div>
                      )}
                      
                      <div className="flex justify-between items-start mb-4">
                        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${getColorClasses(program.category)} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          program.category === 'core' ? 'bg-green-100 text-green-800' :
                          program.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                          program.category === 'cultural' ? 'bg-yellow-100 text-yellow-800' :
                          program.category === 'creative' ? 'bg-pink-100 text-pink-800' :
                          program.category === 'premium' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {program.category}
                        </span>
                      </div>
                      
                      <h3 className="font-bold text-gray-900 text-lg mb-2">
                        {program.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{program.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaClock className="w-4 h-4 mr-2 text-green-500" />
                          {program.duration}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FaUsers className="w-4 h-4 mr-2 text-blue-500" />
                          {program.currentEnrollment || 0}/{program.capacity} enrolled
                        </div>
                        {program.price && program.price.amount > 0 && (
                          <div className="flex items-center text-sm text-gray-600">
                            <FaCrown className="w-4 h-4 mr-2 text-yellow-500" />
                            {program.price.currency} {program.price.amount.toLocaleString()}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          program.enrollmentStatus === 'open' ? 'bg-green-100 text-green-800' :
                          program.enrollmentStatus === 'closed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {program.enrollmentStatus}
                        </span>
                        
                        {(() => {
                          const isEnrolledInPrograms = enrolledPrograms.some(ep => ep._id === program._id || ep.programId === program._id);
                          const isEnrolledInUsers = program.enrolledUsers?.some(eu => eu.userId === user._id);
                          const isEnrolled = isEnrolledInPrograms || isEnrolledInUsers;
                          
                          return isEnrolled ? (
                            <span className="flex items-center text-green-600 font-semibold text-sm">
                              <FaCheck className="w-4 h-4 mr-1" />
                              Enrolled
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">
                              Click to view
                            </span>
                          );
                        })()}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {filteredPrograms.length === 0 && (
                <div className="text-center py-12">
                  <FaGraduationCap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Programs Found</h3>
                  <p className="text-gray-600 mb-4">
                    {programFilter === 'all' 
                      ? 'No programs are currently available. Check back later!' 
                      : `No ${programFilter} programs found. Try a different filter.`}
                  </p>
                  {programFilter !== 'all' && (
                    <button
                      onClick={() => setProgramFilter('all')}
                      className="px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                    >
                      View All Programs
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Live Sessions Tab */}
          {!loading && activeTab === 'live-sessions' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Live Sessions</h2>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    activeLiveSessions.length > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {activeLiveSessions.length} Live Now
                  </span>
                </div>
              </div>

              {liveSessions.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {liveSessions.map((session) => (
                    <motion.div
                      key={session._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-6 rounded-3xl border-2 transition-all duration-300 ${
                        session.isLive 
                          ? 'border-red-200 bg-gradient-to-br from-red-50 to-pink-50 shadow-lg' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                            session.isLive ? 'bg-red-500' : 'bg-gray-400'
                          }`}>
                            <FaVideo className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 text-lg">{session.title}</h3>
                            <p className="text-sm text-gray-600 capitalize">{session.category}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          session.isLive 
                            ? 'bg-red-500 text-white animate-pulse' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {session.isLive ? 'LIVE' : 'ENDED'}
                        </span>
                      </div>

                      <p className="text-gray-600 text-sm mb-4">{session.description}</p>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaUsers className="w-4 h-4 mr-2 text-blue-500" />
                          {session.participants?.length || 0}/{session.maxParticipants} participants
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FaClock className="w-4 h-4 mr-2 text-green-500" />
                          {session.timePerRound} min per round • {session.roundsCount} rounds
                        </div>
                        {session.currentRound && (
                          <div className="flex items-center text-sm text-gray-600">
                            <FaTrophy className="w-4 h-4 mr-2 text-yellow-500" />
                            Round {session.currentRound}/{session.roundsCount}
                          </div>
                        )}
                      </div>

                      {session.isLive ? (
                        <button
                          onClick={() => handleJoinLiveSession(session)}
                          className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white py-3 px-6 rounded-2xl hover:from-red-600 hover:to-pink-700 font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
                        >
                          <FaPlay className="w-4 h-4" />
                          <span>Join Live Session</span>
                        </button>
                      ) : (
                        <button
                          className="w-full bg-gray-100 text-gray-500 py-3 px-6 rounded-2xl font-semibold cursor-not-allowed"
                          disabled
                        >
                          Session Ended
                        </button>
                      )}

                      {session.streamUrl && session.isLive && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <a
                            href={session.streamUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-xs"
                          >
                            <FaBroadcastTower className="w-3 h-3" />
                            <span>External Stream</span>
                          </a>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaVideo className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Live Sessions</h3>
                  <p className="text-gray-600 mb-4">No live sessions are currently available. Check back later!</p>
                </div>
              )}

              {/* Live Sessions with Leaderboards */}
              {liveSessions.filter(s => s.isLive).map(session => (
                <div key={session._id} className="space-y-4">
                  <LiveLeaderboard sessionId={session._id} />
                  <UserResults sessionId={session._id} userId={user._id} />
                </div>
              ))}
            </div>
          )}

          {/* Events Tab - COMPLETELY FIXED */}
          {!loading && activeTab === 'events' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Available Events</h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <FaFilter className="w-4 h-4 text-gray-500" />
                    <select
                      value={eventFilter}
                      onChange={(e) => setEventFilter(e.target.value)}
                      className="border border-gray-300 rounded-2xl px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">All Events</option>
                      <option value="registered">My Registered Events</option>
                      <option value="featured">Featured</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="available">Available to Join</option>
                      <option value="tournament">Tournaments</option>
                      <option value="workshop">Workshops</option>
                      <option value="training">Training</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-600 font-semibold">{filteredEvents.length} Events</span>
                    <FaFire className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredEvents.map((event) => (
                  <motion.div
                    key={event._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-6 rounded-3xl border-2 transition-all duration-300 cursor-pointer hover:shadow-md ${
                      event.featured 
                        ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowEventModal(true);
                    }}
                  >
                    {event.featured && (
                      <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                        FEATURED
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-gray-900 text-lg">
                        {event.title}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        event.type === 'tournament' ? 'bg-purple-100 text-purple-800' :
                        event.type === 'workshop' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {event.type}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaCalendarAlt className="w-4 h-4 mr-2 text-green-500" />
                        {new Date(event.date).toLocaleDateString()} at {event.time}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FaMapMarkerAlt className="w-4 h-4 mr-2 text-blue-500" />
                        {event.location}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FaUserFriends className="w-4 h-4 mr-2 text-purple-500" />
                        {event.participants?.length || 0}/{event.maxParticipants} participants
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        event.difficulty === 'elite' ? 'bg-red-100 text-red-800' :
                        event.difficulty === 'advanced' ? 'bg-orange-100 text-orange-800' :
                        event.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        event.difficulty === 'beginner' ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {event.difficulty}
                      </span>
                      
                      {/* FIXED: Simple status indicator - NO BUTTONS HERE */}
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isRegisteredForEvent(event) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {isRegisteredForEvent(event) ? 'Registered' : 'Available'}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {filteredEvents.length === 0 && (
                <div className="text-center py-12">
                  <FaCalendarAlt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Events Found</h3>
                  <p className="text-gray-600 mb-4">
                    {eventFilter === 'all' 
                      ? 'No events are currently available. Check back later!' 
                      : `No ${eventFilter} events found. Try a different filter.`}
                  </p>
                  {eventFilter !== 'all' && (
                    <button
                      onClick={() => setEventFilter('all')}
                      className="px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                    >
                      View All Events
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {/* My Events Tab (existing content) */}
          {!loading && activeTab === 'my-events' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">My Registered Events</h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <FaFilter className="w-4 h-4 text-gray-500" />
                    <select
                      value={myEventFilter}
                      onChange={(e) => setMyEventFilter(e.target.value)}
                      className="border border-gray-300 rounded-2xl px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="all">All My Events</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="completed">Completed</option>
                      <option value="tournament">Tournaments</option>
                      <option value="workshop">Workshops</option>
                      <option value="training">Training</option>
                    </select>
                  </div>
                  <span className="text-green-600 font-semibold">{filteredMyEvents.length} Registered</span>
                </div>
              </div>

              {filteredMyEvents.length > 0 ? (
                <div className="space-y-4">
                  {filteredMyEvents.map((event) => (
                    <motion.div
                      key={event._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 rounded-2xl bg-green-500 flex items-center justify-center">
                            <FaCheckCircle className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{event.title}</h4>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center">
                                <FaCalendarAlt className="w-4 h-4 mr-1" />
                                {new Date(event.date).toLocaleDateString()}
                              </span>
                              <span className="flex items-center">
                                <FaClock className="w-4 h-4 mr-1" />
                                {event.time}
                              </span>
                              <span className="flex items-center">
                                <FaMapMarkerAlt className="w-4 h-4 mr-1" />
                                {event.location}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            new Date(event.date) > new Date()
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {new Date(event.date) > new Date() ? 'Upcoming' : 'Completed'}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowEventModal(true);
                            }}
                            className="px-4 py-2 bg-green-50 text-green-600 border border-green-200 rounded-2xl hover:bg-green-100 transition-colors"
                            title="View event details"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Are you sure you want to unregister from "${event.title}"?`)) {
                                handleEventUnregister(event._id);
                              }
                            }}
                            className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-2xl hover:bg-red-100 transition-colors"
                            title="Unregister from event"
                          >
                            <FaTimes className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaRegCalendarPlus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {myEventFilter === 'all' ? 'No Events Registered' : `No ${myEventFilter} Events`}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {myEventFilter === 'all' 
                      ? 'Start by registering for upcoming events' 
                      : `No ${myEventFilter} events found. Try a different filter.`}
                  </p>
                  <div className="space-x-2">
                    <button
                      onClick={() => handleNavigation('events')}
                      className="px-6 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                    >
                      Browse Events
                    </button>
                    {myEventFilter !== 'all' && (
                      <button
                        onClick={() => setMyEventFilter('all')}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                      >
                        View All My Events
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* achievements tab*/}
          {!loading && activeTab === 'achievements' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Achievements & Awards</h2>
              
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">My Wins</h3>
                
                {(() => {
                  // Enhanced winner filtering logic
                  const userWins = winners.filter(winner => {
                    
                    // Match by userId
                    if (winner.userId === user?._id) {
                      return true;
                    }
                    
                    // Match by winner name (case insensitive, partial match)
                    const winnerName = winner.winnerName?.toLowerCase().trim();
                    const userName = (user?.name || user?.teamName || user?.schoolName || '').toLowerCase().trim();
                    
                    if (winnerName && userName) {
                      if (winnerName.includes(userName) || userName.includes(winnerName)) {
                        return true;
                      }
                    }
                    
                    // Match by email
                    if (winner.email && winner.email === user?.email) {
                      return true;
                    }
                    
                    return false;
                  });
                  
                  
                  return userWins.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userWins.map((win, index) => (
                        <div key={win._id || index} className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                              {win.position === '1st' ? <FaCrown className="w-5 h-5 text-white" /> :
                               win.position === '2nd' ? <FaMedal className="w-5 h-5 text-white" /> :
                               <FaAward className="w-5 h-5 text-white" />}
                            </div>
                            <div>
                              <p className="font-bold text-yellow-800">{win.position} Place</p>
                              <p className="text-sm text-yellow-700">{win.eventTitle || 'Competition'}</p>
                            </div>
                          </div>
                          <p className="text-sm text-yellow-600">Prize: {win.prize || 'Award'}</p>
                          <p className="text-xs text-yellow-500 mt-1">
                            Winner: {win.winnerName}
                          </p>
                          {win.createdAt && (
                            <p className="text-xs text-yellow-500">
                              Date: {new Date(win.createdAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaTrophy className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">No achievements yet. Keep participating to earn awards!</p>
                      <p className="text-sm text-gray-500">
                        Tip: Make sure your name matches exactly as registered when winners are announced.
                      </p>
                      <div className="mt-4 p-4 bg-blue-50 rounded-2xl">
                        <p className="text-sm text-blue-800">
                          <strong>Debug Info:</strong><br/>
                          Your registered name: {user?.name || user?.teamName || user?.schoolName || 'Not set'}<br/>
                          Your ID: {user?._id}<br/>
                          Total winners in system: {winners.length}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Winners</h3>
                {winners.length > 0 ? (
                  <div className="space-y-3">
                    {winners.slice(0, 10).map((winner, index) => (
                      <div key={winner._id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            winner.position === '1st' ? 'bg-yellow-500' :
                            winner.position === '2nd' ? 'bg-gray-400' : 'bg-amber-600'
                          }`}>
                            <span className="text-white text-xs font-bold">{winner.position?.[0] || '?'}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{winner.winnerName || 'Unknown Winner'}</p>
                            <p className="text-sm text-gray-600">{winner.eventTitle || 'Competition'}</p>
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">{winner.prize || 'Award'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaTrophy className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No winners announced yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Profile Tab (existing content) */}
          {!loading && activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
                <button
                  onClick={() => setEditingProfile(!editingProfile)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                >
                  <FaEdit className="w-4 h-4" />
                  <span>{editingProfile ? 'Cancel Edit' : 'Edit Profile'}</span>
                </button>
              </div>
              
              {editingProfile ? (
                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Profile Information</h3>
                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter your email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter your phone number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                        <input
                          type="text"
                          value={profileData.school}
                          onChange={(e) => setProfileData({...profileData, school: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter your school"
                        />
                      </div>
                    </div>
                    
                    <div className="flex space-x-4 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-green-500 text-white py-3 px-6 rounded-2xl hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                      >
                        {loading ? (
                          <FaSpinner className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <FaCheck className="w-4 h-4" />
                            <span>Save Changes</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingProfile(false)}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">User Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      <p className="text-gray-900">{user?.name || user?.teamName || user?.schoolName || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <p className="text-gray-900">{user?.email || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Registration Type</label>
                      <p className="text-gray-900 capitalize">{user?.registrationType || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        user?.status === 'active' ? 'bg-green-100 text-green-800' :
                        user?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {user?.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">{myEvents.length}</div>
                    <p className="text-gray-600">Events Registered</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600 mb-2">
                      {(() => {
                        const userWins = winners.filter(w => {
                          if (w.userId === user?._id) return true;
                          const winnerName = w.winnerName?.toLowerCase().trim();
                          const userName = (user?.name || user?.teamName || user?.schoolName || '').toLowerCase().trim();
                          if (winnerName && userName && (winnerName.includes(userName) || userName.includes(winnerName))) return true;
                          if (w.email && w.email === user?.email) return true;
                          return false;
                        });
                        return userWins.length;
                      })()}
                    </div>
                    <p className="text-gray-600">Competitions Won</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {(() => {
                        const userWins = winners.filter(w => {
                          if (w.userId === user?._id) return true;
                          const winnerName = w.winnerName?.toLowerCase().trim();
                          const userName = (user?.name || user?.teamName || user?.schoolName || '').toLowerCase().trim();
                          if (winnerName && userName && (winnerName.includes(userName) || userName.includes(winnerName))) return true;
                          if (w.email && w.email === user?.email) return true;
                          return false;
                        });
                        return userWins.length * 10;
                      })()}
                    </div>
                    <p className="text-gray-600">Total Points</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {!loading && activeTab === 'notifications' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllNotificationsAsRead}
                    className="px-3 py-1 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors text-sm"
                  >
                    Mark All Read
                  </button>
                )}
              </div>
              
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                {notifications.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {notifications.map((notification) => (
                      <div
                        key={notification._id}
                        className={`p-3 hover:bg-gray-50 transition-colors ${
                          !notification.read ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            notification.type === 'success' ? 'bg-green-400' :
                            notification.type === 'warning' ? 'bg-yellow-400' :
                            notification.type === 'error' ? 'bg-red-400' : 'bg-blue-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-medium text-gray-900 text-sm truncate">{notification.title}</h3>
                              <div className="flex items-center space-x-2 ml-2">
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                  {new Date(notification.createdAt).toLocaleDateString()}
                                </span>
                                {!notification.read && (
                                  <button
                                    onClick={() => markNotificationAsRead(notification._id)}
                                    className="text-green-600 hover:text-green-700 text-xs font-medium"
                                  >
                                    Mark read
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed">{notification.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <FaBell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <h3 className="font-medium text-gray-900 mb-1">No Notifications</h3>
                    <p className="text-gray-600 text-sm">You're all caught up!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {!loading && activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
              
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferences</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Event Reminders</h4>
                      <p className="text-sm text-gray-600">Get reminders before your registered events</p>
                    </div>
                    <button className="bg-green-500 relative inline-flex items-center h-6 rounded-full w-11">
                      <span className="translate-x-6 inline-block w-4 h-4 transform bg-white rounded-full transition"></span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>
                <div className="space-y-4">
                  <button className="w-full md:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors">
                    Download My Data
                  </button>
                  <button className="w-full md:w-auto px-6 py-2 bg-red-500 text-white rounded-2xl hover:bg-red-600 transition-colors ml-0 md:ml-3">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}

      {/* Event Detail Modal */}
      <AnimatePresence>
        {showEventModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h2>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <FaCalendarAlt className="w-4 h-4 mr-2" />
                        {new Date(selectedEvent.date).toLocaleDateString()} at {selectedEvent.time}
                      </span>
                      <span className="flex items-center">
                        <FaMapMarkerAlt className="w-4 h-4 mr-2" />
                        {selectedEvent.location}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-2 rounded-2xl hover:bg-gray-100"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700">{selectedEvent.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Event Type</h4>
                      <p className="text-gray-700 capitalize">{selectedEvent.type}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Difficulty</h4>
                      <p className="text-gray-700 capitalize">{selectedEvent.difficulty}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Participants</h4>
                      <p className="text-gray-700">
                        {selectedEvent.participants?.length || 0}/{selectedEvent.maxParticipants}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Prize</h4>
                      <p className="text-gray-700">{selectedEvent.prize}</p>
                    </div>
                  </div>

                  <div className="flex space-x-4 pt-4">
                    {isEventPast(selectedEvent) ? (
                      <div className="flex-1 bg-gray-300 text-gray-600 py-3 px-6 rounded-2xl font-semibold text-center cursor-not-allowed">
                        <FaTimes className="inline-block mr-2" /> Event Ended
                      </div>
                    ) : myEvents.some(me => me._id === selectedEvent._id) ? (
                      <button
                        className="flex-1 bg-red-500 text-white py-3 px-6 rounded-2xl hover:bg-red-600 font-semibold transition-colors flex items-center justify-center space-x-2"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to unregister from this event?')) {
                            handleEventUnregister(selectedEvent._id);
                          }
                        }}
                        disabled={loading}
                      >
                        {loading ? (
                          <FaSpinner className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <FaTimes className="w-5 h-5" />
                            <span>Unregister</span>
                          </>
                        )}
                      </button>
                    ) : user?.status === 'active' ? (
                      <button 
                        className="flex-1 bg-green-500 text-white py-3 px-6 rounded-2xl hover:bg-green-600 font-semibold transition-colors flex items-center justify-center space-x-2"
                        onClick={() => handleEventRegistration(selectedEvent._id)}
                        disabled={loading}
                      >
                        {loading ? (
                          <FaSpinner className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <FaRegCalendarPlus className="w-5 h-5" />
                            <span>Register for Event</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="flex-1 bg-yellow-100 text-yellow-800 py-3 px-6 rounded-2xl font-semibold text-center">
                        Complete registration to register for events
                      </div>
                    )}
                    <button 
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                      onClick={() => setShowEventModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Program Detail Modal */}
      <AnimatePresence>
        {showProgramModal && selectedProgram && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowProgramModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedProgram.title}</h2>
                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedProgram.category === 'core' ? 'bg-green-100 text-green-800' :
                        selectedProgram.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                        selectedProgram.category === 'cultural' ? 'bg-yellow-100 text-yellow-800' :
                        selectedProgram.category === 'creative' ? 'bg-pink-100 text-pink-800' :
                        selectedProgram.category === 'premium' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedProgram.category}
                      </span>
                      <span className="text-sm text-gray-600">{selectedProgram.duration}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowProgramModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-2 rounded-2xl hover:bg-gray-100"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700">{selectedProgram.description}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Program Features</h3>
                    <ul className="space-y-2">
                      {selectedProgram.features?.map((feature, index) => (
                        <li key={index} className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-3" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Enrollment</h4>
                      <p className="text-gray-700">
                        {selectedProgram.currentEnrollment || 0}/{selectedProgram.capacity} students
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Status</h4>
                      <p className={`capitalize font-medium ${
                        selectedProgram.enrollmentStatus === 'open' ? 'text-green-600' :
                        selectedProgram.enrollmentStatus === 'closed' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {selectedProgram.enrollmentStatus}
                      </p>
                    </div>
                    {selectedProgram.price && selectedProgram.price.amount > 0 && (
                      <div className="bg-gray-50 p-4 rounded-2xl">
                        <h4 className="font-semibold text-gray-900 mb-1">Price</h4>
                        <p className="text-gray-700">
                          {selectedProgram.price.currency} {selectedProgram.price.amount.toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div className="bg-gray-50 p-4 rounded-2xl">
                      <h4 className="font-semibold text-gray-900 mb-1">Target Age</h4>
                      <p className="text-gray-700">
                        {selectedProgram.targetAge?.min || 5} - {selectedProgram.targetAge?.max || 18} years
                      </p>
                    </div>
                  </div>

                  <div className="flex space-x-4 pt-4">
                    {(() => {
                      const isEnrolledInPrograms = enrolledPrograms.some(ep => ep._id === selectedProgram._id || ep.programId === selectedProgram._id);
                      const isEnrolledInUsers = selectedProgram.enrolledUsers?.some(eu => eu.userId === user._id);
                      const isEnrolled = isEnrolledInPrograms || isEnrolledInUsers;
                      
                      if (isEnrolled) {
                        return (
                          <>
                            <div className="flex-1 bg-green-100 text-green-800 py-3 px-6 rounded-2xl font-semibold text-center flex items-center justify-center space-x-2">
                              <FaCheck className="w-5 h-5" />
                              <span>Already Enrolled</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to unenroll from "${selectedProgram.title}"?`)) {
                                  handleProgramUnenroll(selectedProgram._id);
                                }
                              }}
                              className="px-6 py-3 bg-red-50 text-red-600 border border-red-200 rounded-2xl hover:bg-red-100 transition-colors font-semibold flex items-center space-x-2"
                              disabled={loading}
                              title="Unenroll from program"
                            >
                              <FaTimes className="w-5 h-5" />
                              <span>Unenroll</span>
                            </button>
                          </>
                        );
                      } else if (selectedProgram.enrollmentStatus === 'open' && user?.status === 'active') {
                        return (
                          <button 
                            className={`flex-1 py-3 px-6 rounded-2xl font-semibold transition-colors flex items-center justify-center space-x-2 bg-gradient-to-r ${getColorClasses(selectedProgram.category)} text-white hover:opacity-90`}
                            onClick={() => handleProgramEnrollment(selectedProgram._id)}
                            disabled={loading}
                          >
                            {loading ? (
                              <FaSpinner className="w-5 h-5 animate-spin" />
                            ) : (
                              <>
                                <FaUsers className="w-5 h-5" />
                                <span>Enroll in Program</span>
                              </>
                            )}
                          </button>
                        );
                      } else if (selectedProgram.enrollmentStatus === 'closed') {
                        return (
                          <div className="flex-1 bg-red-100 text-red-800 py-3 px-6 rounded-2xl font-semibold text-center">
                            Enrollment Closed
                          </div>
                        );
                      } else if (user?.status !== 'active') {
                        return (
                          <div className="flex-1 bg-yellow-100 text-yellow-800 py-3 px-6 rounded-2xl font-semibold text-center">
                            Complete registration to enroll
                          </div>
                        );
                      } else {
                        return (
                          <div className="flex-1 bg-gray-100 text-gray-600 py-3 px-6 rounded-2xl font-semibold text-center">
                            Enrollment Full
                          </div>
                        );
                      }
                    })()}
                    <button 
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                      onClick={() => setShowProgramModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Live Session Player Modal */}
      <LiveSessionPlayer
        session={selectedLiveSession}
        isOpen={showLiveModal}
        onClose={() => setShowLiveModal(false)}
        likes={likes}
        onLike={handleLike}
        onShare={handleShare}
      />
    </div>
  );
};

export default Dashboard;
